import * as fromAppInitial from './appInitial';
import * as fromFlightBookingPanel from './shared/flightBookingPanel';
import * as fromFlightSearchResults from './shared/flightSearchResults';
import * as fromFps from './shared/fps';
import * as fromNetwork from './shared/network';
import * as fromSaveToList from './shared/saveToList';

import type { AttributeType } from '@web-platform/shared-universal-constants/src/filterAttributes';
import type { PageGroup } from '@web-platform/shared-universal-constants/src/pageGroups';
import type {
  MutationType,
  QueryType,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import type { Variables } from '@web-platform/shared-universal-types/src/network/types';
import type { CurrentFlightItineraryIdsState } from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entityCollections/currentFlightItineraryIds';
import type { InitialLocationState } from '@web-platform/shared-universal-types/src/redux/state/appInitial/initialLocation';
import type { LocationState } from '@web-platform/shared-universal-types/src/redux/state/appInitial/location';
import type { DeviceState } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/device';
import type { EventsState } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/events';
import type { IdentifiersState } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/identifiers';
import type { LoginState } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/login';
import type { SearchState } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/search';
import type { TrustedFunnelState } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/trustedFunnel';
import type { UIState } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/ui';
import type { Alliance } from '@web-platform/shared-universal-types/src/redux/state/entities/flights/alliances';
import type {
  Carrier,
  CarrierMultipleAggregate,
} from '@web-platform/shared-universal-types/src/redux/state/entities/flights/carriers';
import type {
  Itinerary,
  ItineraryById,
} from '@web-platform/shared-universal-types/src/redux/state/entities/flights/itineraries';
import type { Leg } from '@web-platform/shared-universal-types/src/redux/state/entities/flights/legs';
import type { Segment } from '@web-platform/shared-universal-types/src/redux/state/entities/flights/segments';
import type { AirportsFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/airports';
import type { AlliancesFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/alliances';
import type { CarriersFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/carriers';
import type { DurationFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/duration';
import type { EcoFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/eco';
import type { LayoversFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/layovers';
import type { SelectedSortState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/sort/sort';
import type { MutationState } from '@web-platform/shared-universal-types/src/redux/state/network/mutations';
import type { QueryItem } from '@web-platform/shared-universal-types/src/redux/state/network/queries';
import type { Place } from '@web-platform/shared-universal-types/src/redux/state/places/places';
import type { PriceBandsState } from '@web-platform/shared-universal-types/src/redux/state/priceBands/priceBands';
import type { AgentBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/agentsBookingPanel';
import type { CarrierBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/carriersBookingPanel';
import type {
  ErrorType,
  FilterStatusMapBookingPanel,
  ItineraryBookingPanel,
  ItineraryByIdBookingPanel,
} from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/itinerariesBookingPanel';
import type { LegBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/legsBookingPanel';
import type { SegmentBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/segmentsBookingPanel';
import type { PartnerMetadataTrustLevelBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/partnerMetadataBookingPanel';
import type { PaymentDiscountsBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/paymentDiscountsBookingPanel';
import type { PlaceBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/placesBookingPanel';
import type { TransitionedToFlightBookingPanelState } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/transitionedToFlightBookingPanel';
import type { Agent } from '@web-platform/shared-universal-types/src/redux/state/shared/flightSearchResults/agents';
import type { StateType } from '@web-platform/shared-universal-types/src/redux/state/state-shared-types';
import type { EntityId, Nullable } from '@web-platform/shared-universal-types-utils/src/utils';

export const getUserSearch = (state: StateType): SearchState =>
  fromAppInitial.getUserSearch(state.appInitial);

// appInitial - pageGroup
export const getPageGroup = (state: StateType): PageGroup =>
  fromAppInitial.getPageGroup(state.appInitial);

// appInitial - initialLocation
export const getInitialLocation = (state: StateType): InitialLocationState =>
  fromAppInitial.getInitialLocation(state.appInitial);

// appInitial - location
export const getLocation = (state: StateType): LocationState =>
  fromAppInitial.getLocation(state.appInitial);

// appInitial - user - device
export const getUserDevice = (state: StateType): DeviceState =>
  fromAppInitial.getUserDevice(state.appInitial);

// appInitial - user - events
export const getUserEvents = (state: StateType): EventsState =>
  fromAppInitial.getUserEvents(state.appInitial);

// appInitial - user - identifiers
export const getUserIdentifiers = (state: StateType): IdentifiersState =>
  fromAppInitial.getUserIdentifiers(state.appInitial);

// appInitial - user - login
export const getLogin = (state: StateType): LoginState =>
  fromAppInitial.getUserLogin(state.appInitial);

// appInitial - user - trustedFunnel
export const getTrustedFunnel = (state: StateType): TrustedFunnelState =>
  fromAppInitial.getUserTrustedFunnel(state.appInitial);

// appInitial - user - ui
export const getUserUI = (state: StateType): UIState => fromAppInitial.getUserUI(state.appInitial);

// appInitial - user - session
export const getSession = (state: StateType, key: MutationType) =>
  fromAppInitial.getUserSession(state.appInitial, key);

// shared - network
export const getAllMutations = (state: StateType): MutationState =>
  fromNetwork.getAllMutations(state.shared.network);

export const getQuery = <QT extends QueryType>(
  state: StateType,
  type: QT,
  variables: Variables,
): (QueryItem & { type: QT }) | undefined =>
  fromNetwork.getQuery(state.shared.network, type, variables);

// shared - flightSearchResults - agents
export const getAgent = (state: StateType, id: string): Agent | undefined =>
  fromFlightSearchResults.agent(state.shared.flightSearchResults, id);
export const getAllAgents = (state: StateType): Record<string, Agent> =>
  fromFlightSearchResults.allAgents(state.shared.flightSearchResults);

// shared - flightSearchResults - flights
export const flightLeg = (state: StateType, id: string): Leg | undefined =>
  fromFlightSearchResults.flightLeg(state.shared.flightSearchResults, id);
export const flightAllCarriers = (state: StateType): Record<number, Carrier> =>
  fromFlightSearchResults.flightAllCarriers(state.shared.flightSearchResults);
export const flightCarriersMultiple = (state: StateType): CarrierMultipleAggregate | null =>
  fromFlightSearchResults.flightCarriersMultiple(state.shared.flightSearchResults);
export const flightAllLegs = (state: StateType): Record<string, Leg> =>
  fromFlightSearchResults.flightAllLegs(state.shared.flightSearchResults);
export const flightAllSegments = (state: StateType): Record<string, Segment> =>
  fromFlightSearchResults.flightAllSegments(state.shared.flightSearchResults);
export const flightItinerary = (state: StateType, id: string): Itinerary =>
  fromFlightSearchResults.flightItinerary(state.shared.flightSearchResults, id);
export const flightAllItineraries = (state: StateType): ItineraryById =>
  fromFlightSearchResults.flightAllItineraries(state.shared.flightSearchResults);
export const getCurrentFlightItineraryIds = (state: StateType): CurrentFlightItineraryIdsState =>
  fromFlightSearchResults.getCurrentFlightItineraryIds(state.shared.flightSearchResults);

// shared - flightSearchResults - sort
export const getSelectedSort = (state: StateType): SelectedSortState =>
  fromFlightSearchResults.getSelectedSort(state.shared.flightSearchResults);

// shared - michelin - hotels
export const getMichelinHotels = (state: StateType): StateType['shared']['michelin']['hotels'] =>
  state.shared.michelin.hotels;

// shared - flightSearchResults - places
export const flightPlaceByEntityId = (state: StateType, entityId: EntityId): Place =>
  fromFlightSearchResults.flightPlaceByEntityId(state.shared.flightSearchResults, entityId);

export const flightAllPlacesByEntityId = (state: StateType): Record<EntityId, Place> =>
  fromFlightSearchResults.flightAllPlacesByEntityId(state.shared.flightSearchResults);

// shared - flightSearchResults - partnerMetadata
export const getPartnerMetadata = (state: StateType, id: string) =>
  fromFlightSearchResults.getPartnerMetadata(state.shared.flightSearchResults, id);
export const getAllPartnerMetadata = (state: StateType) =>
  fromFlightSearchResults.getAllPartnerMetadata(state.shared.flightSearchResults);

// shared - flightSearchResults - pricePulse
export const getPricePulse = (state: StateType, id: string) =>
  fromFlightSearchResults.getPricePulse(state.shared.flightSearchResults, id);
export const getPricePulseByIdMap = (state: StateType) =>
  fromFlightSearchResults.getPricePulseByIdMap(state.shared.flightSearchResults);

// shared - flightSearchResults - single leg results
export const getSingleLegResults = (state: StateType) =>
  fromFlightSearchResults.getSingleLegResults(state.shared.flightSearchResults);

// shared - flightSearchResults - alternativeDates
export const getAlternativeDates = (state: StateType) =>
  fromFlightSearchResults.getAlternativeDates(state.shared.flightSearchResults);

// shared - flightSearchResults - priceBands
export const getPriceBands = (state: StateType): PriceBandsState =>
  fromFlightSearchResults.getPriceBands(state.shared.flightSearchResults);

// shared - flightSearchResults - entities - carriers / alliances
export const flightAllAlliances = (state: StateType): Record<number, Alliance> =>
  fromFlightSearchResults.flightAllAlliances(state.shared.flightSearchResults);
export const flightCarriersCurrentResultIds = (state: StateType): number[] =>
  fromFlightSearchResults.flightCarriersCurrentResultIds(state.shared.flightSearchResults);

// shared - flightSearchResults - features - filters - carriers / alliances
export const getCarriersFilter = (state: StateType): CarriersFilterState =>
  fromFlightSearchResults.getCarriersFilter(state.shared.flightSearchResults);
export const getAlliancesFilter = (state: StateType): AlliancesFilterState =>
  fromFlightSearchResults.getAlliancesFilter(state.shared.flightSearchResults);

// shared - flightSearchResults - features - filters
export const getLayoversDurationFilter = (state: StateType): LayoversFilterState['duration'] =>
  fromFlightSearchResults.getLayoversDurationFilter(state.shared.flightSearchResults);
export const getLayoversExcludedAirportsFilter = (
  state: StateType,
): LayoversFilterState['excludedAirports'] =>
  fromFlightSearchResults.getLayoversExcludedAirportsFilter(state.shared.flightSearchResults);

// shared - flightSearchResults - features - filters
export const getAirportsFilter = (state: StateType): AirportsFilterState =>
  fromFlightSearchResults.getAirportsFilter(state.shared.flightSearchResults);
export const getAirportsStatsFilter = (state: StateType) =>
  fromFlightSearchResults.getAirportsStatsFilter(state.shared.flightSearchResults);

// shared - flightSearchResults - features - filters - mixedAirport
export const getMixedAirportFilterState = (state: StateType) =>
  fromFlightSearchResults.getMixedAirportFilter(state.shared.flightSearchResults);

// shared - flightSearchResults - features - filters
export const getDurationFilter = (state: StateType): DurationFilterState =>
  fromFlightSearchResults.getDurationFilter(state.shared.flightSearchResults);

// shared - flightSearchResults - features - filters - eco
export const getEcoFilter = (state: StateType): EcoFilterState =>
  fromFlightSearchResults.getEcoFilter(state.shared.flightSearchResults);

// shared - flightSearchResults - features - filters - stops
export const getStopsFilter = (state: StateType) =>
  fromFlightSearchResults.getStopsFilter(state.shared.flightSearchResults);

// shared - flightBookingPanel - legs
export const flightLegBookingPanel = (state: StateType, id: string): LegBookingPanel =>
  fromFlightBookingPanel.flightLegBookingPanel(state.shared.flightBookingPanel, id);
export const flightAllLegsBookingPanel = (state: StateType): Record<string, LegBookingPanel> =>
  fromFlightBookingPanel.flightAllLegsBookingPanel(state.shared.flightBookingPanel);

// shared - flightBookingPanel - carriers
export const flightCarrierBookingPanel = (state: StateType, id: number): CarrierBookingPanel =>
  fromFlightBookingPanel.flightCarrierBookingPanel(state.shared.flightBookingPanel, id);
export const flightAllCarriersBookingPanel = (
  state: StateType,
): Record<number, CarrierBookingPanel> =>
  fromFlightBookingPanel.flightAllCarriersBookingPanel(state.shared.flightBookingPanel);

// shared - flightBookingPanel - segments
export const flightSegmentBookingPanel = (state: StateType, id: string): SegmentBookingPanel =>
  fromFlightBookingPanel.flightSegmentBookingPanel(state.shared.flightBookingPanel, id);
export const flightAllSegmentsBookingPanel = (
  state: StateType,
): Record<string, SegmentBookingPanel> =>
  fromFlightBookingPanel.flightAllSegmentsBookingPanel(state.shared.flightBookingPanel);

// shared - flightBookingPanel - places
export const allPlacesByEntityIdBookingPanel = (
  state: StateType,
): Record<EntityId, PlaceBookingPanel> =>
  fromFlightBookingPanel.allPlacesByEntityIdBookingPanel(state.shared.flightBookingPanel);

// shared - flightBookingPanel - agents
export const agentBookingPanel = (state: StateType, id: string): AgentBookingPanel =>
  fromFlightBookingPanel.agentBookingPanel(state.shared.flightBookingPanel, id);
export const allAgentsBookingPanel = (state: StateType): Record<string, AgentBookingPanel> =>
  fromFlightBookingPanel.allAgentsBookingPanel(state.shared.flightBookingPanel);

// shared - flightBookingPanel - partnerMetadata
export const getPartnerMetadataBookingPanel = (
  state: StateType,
  id: string,
): PartnerMetadataTrustLevelBookingPanel =>
  fromFlightBookingPanel.getPartnerMetadataBookingPanel(state.shared.flightBookingPanel, id);
export const getAllPartnerMetadataBookingPanel = (
  state: StateType,
): Record<string, PartnerMetadataTrustLevelBookingPanel> =>
  fromFlightBookingPanel.getAllPartnerMetadataBookingPanel(state.shared.flightBookingPanel);

// shared - flightBookingPanel - itineraries
export const flightItineraryBookingPanel = (state: StateType, id: string): ItineraryBookingPanel =>
  fromFlightBookingPanel.flightItineraryBookingPanel(state.shared.flightBookingPanel, id);
export const flightAllItinerariesBookingPanel = (state: StateType): ItineraryByIdBookingPanel =>
  fromFlightBookingPanel.flightAllItinerariesBookingPanel(state.shared.flightBookingPanel);
export const getItineraryFilterStatusMapBookingPanel = (
  state: StateType,
  id: string,
): FilterStatusMapBookingPanel[] =>
  fromFlightBookingPanel.getItineraryFilterStatusMapBookingPanel(
    state.shared.flightBookingPanel,
    id,
  );
export const getItineraryErrorBookingPanel = (
  state: StateType,
  id: string,
): ErrorType | undefined =>
  fromFlightBookingPanel.getItineraryErrorBookingPanel(state.shared.flightBookingPanel, id);

// shared - fps
export const hadSuccessfulFPSMutationToBooking = (state: StateType, itineraryId: string): boolean =>
  fromFps.getHadSuccessfulBookingMutation(state.shared.fps, itineraryId);
export const hadErrorFPSMutationToBooking = (state: StateType, itineraryId: string): boolean =>
  fromFps.getHadErrorFPSMutationToBooking(state.shared.fps, itineraryId);
export const getHadCompleteFpsResponse = (state: StateType): boolean =>
  fromFps.getHadCompleteResponse(state.shared.fps);

// shared - flightBookingPanel - paymentDiscounts
export const getPaymentDiscountsForItineraryBookingPanel = (
  state: StateType,
  id: string,
): PaymentDiscountsBookingPanel | undefined =>
  fromFlightBookingPanel.getPaymentDiscountsForItineraryBookingPanel(
    state.shared.flightBookingPanel,
    id,
  );
export const getAvailablePaymentDiscountsForItineraryBookingPanel = (
  state: StateType,
  id: string,
): PaymentDiscountsBookingPanel | undefined =>
  fromFlightBookingPanel.getAvailablePaymentDiscountsForItineraryBookingPanel(
    state.shared.flightBookingPanel,
    id,
  );
export const getFilteredPaymentDiscountsForItineraryBookingPanel = (
  state: StateType,
  id: string,
): PaymentDiscountsBookingPanel | undefined =>
  fromFlightBookingPanel.getFilteredPaymentDiscountsForItineraryBookingPanel(
    state.shared.flightBookingPanel,
    id,
  );
export const getSelectedPaymentMethodFilter = (state: StateType): string[] | undefined =>
  fromFlightBookingPanel.getSelectedPaymentMethodFilter(state.shared.flightBookingPanel);
export const getPaymentDiscountNames = (
  state: StateType,
  id: string,
  enablePaymentDiscounts: boolean,
): string[] =>
  fromFlightBookingPanel.getPaymentDiscountNames(
    state.shared.flightBookingPanel,
    id,
    enablePaymentDiscounts,
  );

// shared - flightBookingPanel - filters
export const getBookingPanelSelectedFilters = (state: StateType): AttributeType[] =>
  fromFlightBookingPanel.getBookingPanelSelectedFilters(state.shared.flightBookingPanel);

// shared - flightBookingPanel - transitionedToFlightBookingPanel
export const getTransitionedToFlightBookingPanel = (
  state: StateType,
): TransitionedToFlightBookingPanelState =>
  fromFlightBookingPanel.getTransitionedToFlightBookingPanel(state.shared.flightBookingPanel);

// shared - adverts
export const getSponsoredAgent = (state: StateType) => state.shared.adverts.sponsoredAgent;

// shared - saveToList
export const getItineraryIdToSaveOnLogIn = (state: StateType): Nullable<string> =>
  fromSaveToList.getItineraryIdToSaveOnLogIn(state.shared.saveToList);
